import React, { useEffect } from "react";
import {
  Bank,
  Button,
  Card,
  Col,
  Drawer,
  Flex,
  Form,
  Grid,
  Input,
  Radio,
  Row,
  Space,
  Typography,
  useMessagedAsyncFn,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import { CheckCircleFilled, Close, CuToman, LockOutlined, LogoPersian } from "@payping/icons";
import { unmaskStrToNum } from "@payping/utils/string";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { SubLayout } from "@/components/SubLayout";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { withdrawApis } from "@payping/api";
import { useAsync } from "react-use";
import { getBalance, getUserBank } from "@payping/redux/actions";
import { DashboardRoutes } from "@/router/constant";
import { useNavigate } from "react-router-dom";
import { submitFormId } from "@/assets/element-ids";
import FixButton from "@/components/Buttons/fixButton";
import { formatIBAN, useLastUrlWasPage } from "@/utils";

interface FormFields {
  amount?: number;
  balanceType: number;
}

const RequestWithdraw: React.FC = () => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { sheba }: any = useAppSelector((state) => state.user.bankInfo);
  const [form] = Form.useForm<FormFields>();
  const isAutoWithdrawActive = useAppSelector((state) => state.user.autoWithdraw === true);
  const { xs } = Grid.useBreakpoint();
  const { navigateToLast } = useLastUrlWasPage();

  const [{ loading: handleRequestWithdrawLoading }, handleRequestWithdraw] = useMessagedAsyncFn(
    (amount: number) => withdrawApis.withdrawCreate(amount),
    [],
  );

  const { value: balance } = useMessagedAsyncRetry(() => dispatch(getBalance()), [dispatch], {
    useProgress: true,
  });

  useEffect(() => {
    form.setFieldsValue({ balanceType: balance, amount: balance });
  }, [balance, form]);

  const { loading } = useAsync(async () => {
    await dispatch(getUserBank());
  }, [dispatch]);
  const onFinish = async (data: FormFields) => {
    // handleRequestWithdrawLoading is for blocking multiple form submits on pressing Enter
    // isAutoWithdrawActive is for disabling request if autoWithdraw is active
    if (handleRequestWithdrawLoading || isAutoWithdrawActive) {
      return;
    }

    const { amount: amountStr } = data;
    const amount = unmaskStrToNum((amountStr ?? "0").toString());
    const res = await handleRequestWithdraw(amount);
    if (res.code) {
      navigateToLast();
    }
  };

  const shabaNumber = sheba;
  const maskedShaba = formatIBAN(shabaNumber);

  return (
    <Drawer
      title={
        <Space>
          <LogoPersian className={"!tw-flex !tw-items-center"} width={70} />
          <Typography.Text strong>{t("mySales.withdrawTab.requestWithdraw")}</Typography.Text>
        </Space>
      }
      closeIcon={false}
      placement={"bottom"}
      height={"100%"}
      open
      extra={
        <Space>
          {!xs ? (
            <Space>
              <Typography.Text type={"secondary"}>{t("mySales.withdrawTab.balance")}</Typography.Text>
              <Flex align="center">
                <Typography.Text strong>{`${formatLocalizedNumber({ value: balance ?? 0 })}`}</Typography.Text>
                <CuToman width={24} height={24} />
              </Flex>
            </Space>
          ) : null}
          <Button
            type={"text"}
            icon={<Close width={14} height={14} />}
            onClick={() => {
              navigateToLast();
            }}
          ></Button>
        </Space>
      }
    >
      <SubLayout className={xs ? "w-100" : "w-100 mt-4"}>
        <Form
          id={submitFormId}
          form={form}
          onFinish={onFinish}
          className={xs ? "justify-center mb-4" : "justify-center"}
          onValuesChange={(changedValues) => {
            if ("balanceType" in changedValues) {
              form.setFieldsValue({ amount: changedValues.balanceType });
            }
          }}
        >
          <Row gutter={[0, 16]} className={"tw-flex tw-w-full tw-max-w-[800px] tw-justify-center"}>
            {xs ? (
              <Col xs={24}>
                <div className={"tw-flex tw-justify-between"}>
                  <Typography.Text type={"secondary"}>{t("mySales.withdrawTab.balance")}</Typography.Text>
                  <Flex align="center">
                    <Typography.Text strong>{`${formatLocalizedNumber({ value: balance ?? 0 })}`}</Typography.Text>
                    <CuToman width={24} height={24} />
                  </Flex>
                </div>
              </Col>
            ) : null}
            {isAutoWithdrawActive ? (
              <Col xs={24}>
                <Card.Ant>
                  <Space>
                    <CheckCircleFilled className={"!tw-flex !tw-items-center"} width={14} color={"green"} />
                    <Typography.Text>{t("withdraw.isEnableAutoWithdraw")}</Typography.Text>
                  </Space>
                </Card.Ant>
              </Col>
            ) : null}
            <Col xs={24}>
              <Card.Ant>
                <Flex vertical align={"center"} gap={"small"}>
                  {/* Title */}
                  <Flex>
                    <Typography.Text type={"secondary"}>{t("mySales.withdrawTab.amountWithdraw")}</Typography.Text>
                  </Flex>
                  {/* Form 1 */}
                  <Row align={"middle"}>
                    <Form.Item
                      dependencies={["balanceType"]}
                      name="amount"
                      className={"!tw-m-0"}
                      rules={[
                        {
                          min: 1_000,
                          required: true,
                          transform: (value): number => unmaskStrToNum(value),
                          type: "number",
                        },
                      ]}
                    >
                      <Input.Number
                        size="large"
                        className="tw-min-w-44"
                        suffix={<CuToman width={24} height={24} />}
                        separateThousandth
                        inputMode="numeric"
                        changeOnWheel={false}
                        controls={false}
                        autoFocus
                        bordered={false}
                        disabled={handleRequestWithdrawLoading || isAutoWithdrawActive}
                      />
                    </Form.Item>
                  </Row>
                  {/* Form 2 */}
                  <Flex vertical gap={"large"} justify={"center"} align={"center"}>
                    {/* Form */}
                    {!isAutoWithdrawActive ? (
                      <Form.Item noStyle name={"balanceType"}>
                        <Radio.Group size={"middle"} className={"tw-w-full"}>
                          <Radio.Button
                            value={balance}
                          >{`${t("mySales.withdrawTab.allBalance")} ${formatLocalizedNumber({
                            value: balance ?? 0,
                          })}`}</Radio.Button>
                          <Radio.Button value={Math.trunc((balance || 0) / 2)}>
                            {formatLocalizedNumber({ value: Math.trunc((balance || 0) / 2) })}
                          </Radio.Button>
                          <Radio.Button value={Math.trunc((balance || 0) / 4)}>
                            {formatLocalizedNumber({ value: Math.trunc((balance || 0) / 4) })}
                          </Radio.Button>
                        </Radio.Group>
                      </Form.Item>
                    ) : null}
                    {/* Text */}
                    <Typography.Text type={"secondary"}>
                      {isAutoWithdrawActive
                        ? t("mySales.withdrawTab.autoWithdrawTip")
                        : t("mySales.withdrawTab.manualWithdrawTip")}
                    </Typography.Text>
                  </Flex>
                </Flex>
              </Card.Ant>
            </Col>
            <Col xs={24}>
              <Typography.Text type={"secondary"}>{t("mySales.withdrawTab.transferToIban")}</Typography.Text>
            </Col>
            <Col xs={24}>
              <Card.Ant loading={loading} className={"tw-w-full"}>
                <Space wrap className={"ltr"}>
                  <Bank.Icon showTooltip iban={sheba} />
                  <Typography.Text className="en-font tw-whitespace-pre-line">{maskedShaba}</Typography.Text>
                </Space>
              </Card.Ant>
            </Col>
            {!isAutoWithdrawActive ? (
              <Col xs={24}>
                <Card.Ant>
                  <Flex vertical gap={"middle"}>
                    {/* < تایتل > */}
                    <Space className={"!tw-flex !tw-justify-between"}>
                      <Space>
                        <LockOutlined width={16} />
                        <Typography.Text>{t("mySales.withdrawTab.autoWithdraw")}</Typography.Text>
                      </Space>
                      <Button onClick={() => navigate(DashboardRoutes.user.upgrade.url)}>
                        {t("mySales.withdrawTab.upgradePanel")}
                      </Button>
                    </Space>

                    {/* < متن > */}
                    <Typography.Text type={"secondary"}>
                      {t("mySales.withdrawTab.activateAutoSettlement")}
                    </Typography.Text>
                  </Flex>
                </Card.Ant>
              </Col>
            ) : null}

            {/* eslint-disable-next-line no-nested-ternary */}
            {!isAutoWithdrawActive ? (
              !xs ? (
                <Col xs={24}>
                  <Space>
                    <Button type={"primary"} loading={handleRequestWithdrawLoading} htmlType={"submit"}>
                      {t("mySales.withdrawTab.sendWithdraw")}
                    </Button>
                    <Button
                      onClick={() => {
                        navigateToLast();
                      }}
                    >
                      {t("cancel")}
                    </Button>
                  </Space>
                </Col>
              ) : (
                <FixButton>
                  <Row gutter={[8, 8]}>
                    <Col flex={3}>
                      <Button type={"primary"} loading={handleRequestWithdrawLoading} htmlType={"submit"} block>
                        {t("mySales.withdrawTab.sendWithdraw")}
                      </Button>
                    </Col>
                    <Col flex={1}>
                      <Button
                        onClick={() => {
                          navigateToLast();
                        }}
                        block
                      >
                        {t("cancel")}
                      </Button>
                    </Col>
                  </Row>
                </FixButton>
              )
            ) : null}
          </Row>
        </Form>
      </SubLayout>
    </Drawer>
  );
};

export default RequestWithdraw;
